import { createSimpleContext } from "@mux/ui/context"
import { showToast } from "@mux/ui/toast"
import { getBooleanPreference, getDefaultWorkspaceSecurity, getDesktopModel, getModelPresets, getNumberPreference, getStringPreference, type DesktopModel } from "@/transport/preferences"
import { createAccountPreference } from "@/context/account-preference"
import type { ModelPreset } from "@/utils/model-presets"
import type { PermissionTrustLevel } from "@mux/sdk/v2/client"
import { createDeviceSettings, type NewChatBehavior } from "./device-settings"
import { describeError } from "@/utils/error-message"

export type { NotificationSettings, NewChatBehavior } from "./device-settings"

export type ModelFallback = "off" | "same-model" | "any-model"

export type ResponseStyle = "normal" | "concise" | "super-short"

/** When a message sent while a turn runs reaches the agent: at the turn's next step, or once it finishes. */
export type FollowupDelivery = "steer" | "queue"

export const { use: useSettings, provider: SettingsProvider } = createSimpleContext({
  name: "Settings",
  init: () => {
    const device = createDeviceSettings()
    const presentation = (key: "stream_compaction_summaries" | "show_reasoning_summaries", initial: boolean) =>
      createAccountPreference(key, initial, () => getBooleanPreference(`/api/ai/preferences/${key}`))
    const compaction = presentation("stream_compaction_summaries", false)
    const reasoning = presentation("show_reasoning_summaries", false)
    const machineDesktop = createAccountPreference("show_machine_desktop", true, () => getBooleanPreference("/api/ai/preferences/show_machine_desktop"))
    const agentToggle = createAccountPreference("show_agent_toggle", false, () => getBooleanPreference("/api/ai/preferences/show_agent_toggle"))
    const followupDelivery = createAccountPreference<FollowupDelivery>("followup_delivery", "steer", async () => {
      const value = await getStringPreference("/api/ai/preferences/followup_delivery")
      if (value !== "steer" && value !== "queue") throw new Error("Invalid follow-up delivery setting")
      return value
    })
    const titleRefresh = createAccountPreference<0 | 5 | 10>("title_refresh_turns", 5, async () => {
      const value = await getNumberPreference("/api/ai/preferences/title_refresh_turns")
      if (value !== 0 && value !== 5 && value !== 10) throw new Error("Invalid title refresh setting")
      return value
    })
    const responseStyle = createAccountPreference<ResponseStyle>("response_style", "normal", async () => {
      const value = await getStringPreference("/api/ai/preferences/response_style")
      if (value !== "normal" && value !== "concise" && value !== "super-short") throw new Error("Invalid response style")
      return value
    })
    const autoArchive = createAccountPreference<number>("auto_archive_days", 7, () => getNumberPreference("/api/ai/preferences/auto_archive_days"))
    // How chats the Oracles start begin when the request does not say.
    const oracleNewChat = createAccountPreference<NewChatBehavior>("oracle_new_chat", "first-fork", async () => {
      const value = await getStringPreference("/api/ai/preferences/oracle_new_chat")
      if (value !== "first-fork" && value !== "new-chat") throw new Error("Invalid Oracle new chat setting")
      return value
    })
    const modelFallback = createAccountPreference<ModelFallback>("model_fallback", "same-model", async () => {
      const value = await getStringPreference("/api/ai/preferences/model_fallback")
      if (value !== "off" && value !== "same-model" && value !== "any-model") throw new Error("Invalid model fallback setting")
      return value
    })
    const modelPresets = createAccountPreference<ModelPreset[]>("model_presets", [], getModelPresets)
    // The model desktop chats run on, until it loads the account's choice.
    const desktopModel = createAccountPreference<DesktopModel | null>("desktop_model", null, getDesktopModel)
    const workspaceSecurity = createAccountPreference<PermissionTrustLevel | null>(
      "default_workspace_security", null, getDefaultWorkspaceSecurity,
    )
    // The machine Mux Oracle turns run on when it is online; "" follows the last machine used.
    const muxOracleMachine = createAccountPreference<string>("mux_oracle_machine", "", () =>
      getStringPreference("/api/ai/preferences/mux_oracle_machine"),
    )
    const reportSave = (operation: Promise<void>) => {
      void operation.catch((error) => showToast({
        title: "Could not save account settings",
        description: describeError(error),
      }))
    }

    return {
      account: {
        autoArchive,
        titleRefresh,
        responseStyle,
        workspaceSecurity,
        compaction,
        reasoning,
        machineDesktop,
        agentToggle,
        followupDelivery,
        modelFallback,
        muxOracleMachine,
      },
      general: {
        modelFallback: modelFallback.value,
        setModelFallback(value: ModelFallback) { reportSave(modelFallback.save(value)) },
        showReasoningSummaries: reasoning.value,
        setShowReasoningSummaries(value: boolean) {
          reportSave(reasoning.save(value))
        },
        streamCompactionSummaries: compaction.value,
        setStreamCompactionSummaries(value: boolean) {
          reportSave(compaction.save(value))
        },
        titleRefreshTurns: titleRefresh.value,
        setTitleRefreshTurns(value: 0 | 5 | 10) {
          reportSave(titleRefresh.save(value))
        },
        responseStyle: responseStyle.value,
        setResponseStyle(value: ResponseStyle) {
          reportSave(responseStyle.save(value))
        },
        showMachineDesktop: machineDesktop.value,
        setShowMachineDesktop(value: boolean) {
          reportSave(machineDesktop.save(value))
        },
      },
      newChat: device.newChat,
      oracleNewChat: {
        behavior: oracleNewChat.value,
        setBehavior(value: NewChatBehavior) {
          reportSave(oracleNewChat.save(value))
        },
      },
      composer: {
        // Plan is entered with /plan; the Build/Plan selector is opt-in.
        showAgentToggle: agentToggle.value,
        setShowAgentToggle(value: boolean) {
          reportSave(agentToggle.save(value))
        },
        followupDelivery: followupDelivery.value,
        setFollowupDelivery(value: FollowupDelivery) {
          reportSave(followupDelivery.save(value))
        },
      },
      sharing: device.sharing,
      sidebar: device.sidebar,
      keybinds: device.keybinds,
      modelPresets: {
        ...modelPresets,
        list: modelPresets.value,
        set(value: ModelPreset[]) {
          reportSave(modelPresets.save(value))
        },
      },
      desktopModel: {
        ...desktopModel,
        set(value: DesktopModel | null) {
          reportSave(desktopModel.save(value))
        },
      },
      notifications: device.notifications,
    }
  },
})
